import {useMutation} from 'react-query'

import {
  mutations,
  ValidateConfirmationPinResponse
} from '../../../../../common/react-query/mutations'

export type PinValidationError = {
  type: 'invalid_pin' | 'network_error'
  message: string
}

export type ValidateConfirmationPinParams = {
  customerId: string
  pin: string
}

/**
 * Hook for validating customer confirmation PIN.
 * Handles API call and differentiates between invalid PIN vs network errors.
 */
export const useValidateConfirmationPin = () => {
  const mutation = useMutation<
    ValidateConfirmationPinResponse,
    PinValidationError,
    ValidateConfirmationPinParams
  >({
    mutationFn: async ({customerId, pin}) => {
      try {
        const response = await mutations.validateConfirmationPin({customerId, pin})
        return response
      } catch (error) {
        // Network or server error
        throw {
          type: 'network_error',
          message: 'confirmationPin.modal.genericError'
        } as PinValidationError
      }
    },
    onError: () => {
      // Error handling is done in the component
    }
  })

  const validatePin = async (
    params: ValidateConfirmationPinParams
  ): Promise<{isValid: boolean; error?: PinValidationError}> => {
    try {
      const response = await mutation.mutateAsync(params)

      if (response.result === 'valid') {
        return {isValid: true}
      }

      return {
        isValid: false,
        error: {
          type: 'invalid_pin',
          message: 'confirmationPin.modal.invalidPinError'
        }
      }
    } catch (error) {
      return {
        isValid: false,
        error: error as PinValidationError
      }
    }
  }

  return {
    validatePin,
    isLoading: mutation.isLoading,
    reset: mutation.reset
  }
}
